import { Minus, Plus, X, ShoppingCart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtMoney } from "@/lib/api";
import { FieldError } from "./field-error";

export function Cart({
  channel,
  cart,
  tableNo,
  onTableNoChange,
  customerId,
  customers,
  onCustomerChange,
  fieldErrors,
  onQtyChange,
  onRemove,
  subtotal,
  children,
}) {
  return (
    <div className="lg:col-span-2 bg-white border border-stone-200 rounded-sm flex flex-col" style={{ maxHeight: "calc(100vh - 200px)" }}>
      <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
        <h3 className="font-serif text-lg font-bold flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-emerald-800" aria-hidden="true" />Giỏ hàng
        </h3>
        <span className="text-xs text-stone-700 font-mono">{cart.length} dòng</span>
      </div>

      {channel === "restaurant" && (
        <div className="px-4 py-2 border-b border-stone-100">
          <Input
            placeholder="Số bàn (VD: B05)"
            value={tableNo}
            onChange={(e) => onTableNoChange(e.target.value)}
            aria-invalid={Boolean(fieldErrors.table_no)}
            aria-label="Số bàn"
            className={`h-9 rounded-sm ${fieldErrors.table_no ? "border-red-400 focus-visible:ring-red-500" : ""}`}
            data-testid="pos-table-no"
          />
          <FieldError message={fieldErrors.table_no} />
        </div>
      )}

      {channel === "b2b" && (
        <div className="px-4 py-2 border-b border-stone-100">
          <Select value={customerId} onValueChange={onCustomerChange}>
            <SelectTrigger
              aria-invalid={Boolean(fieldErrors.customer_id)}
              aria-label="Khách hàng B2B"
              className={`h-9 rounded-sm ${fieldErrors.customer_id ? "border-red-400 focus:ring-red-500" : ""}`}
            >
              <SelectValue placeholder="Chọn khách hàng B2B" />
            </SelectTrigger>
            <SelectContent>
              {customers.filter((c) => c.type === "B2B").map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={fieldErrors.customer_id} />
        </div>
      )}

      <div className="flex-1 overflow-y-auto divide-y divide-stone-100">
        {cart.map((l, index) => (
          <CartLine
            key={l.sku_id}
            line={l}
            index={index}
            fieldErrors={fieldErrors}
            onQtyChange={onQtyChange}
            onRemove={onRemove}
          />
        ))}
        {cart.length === 0 && <div className="text-center text-stone-700 py-10 text-sm">Chưa có món/SKU nào</div>}
        <FieldError message={fieldErrors.lines} />
      </div>

      <div className="border-t border-stone-200 p-4 space-y-2 bg-stone-50">
        <div className="flex justify-between text-base font-bold">
          <span>Tổng cộng</span>
          <span className="font-mono text-emerald-800" data-testid="cart-subtotal">{fmtMoney(subtotal)}</span>
        </div>
        {children}
      </div>
    </div>
  );
}

function CartLine({ line, index, fieldErrors, onQtyChange, onRemove }) {
  const minusDisabled = line.qty <= 1;
  return (
    <div className="px-4 py-2 flex items-center gap-2" data-testid={`cart-line-${line.sku_code}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{line.sku_name}</div>
        <div className="text-[10px] text-stone-700 font-mono">{line.sku_code} • {fmtMoney(line.unit_price)}</div>
        <FieldError message={fieldErrors[`lines.${index}.qty`] || fieldErrors[`lines.${index}.unit_price`]} />
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onQtyChange(line.sku_id, -1)}
          disabled={minusDisabled}
          aria-label={`Giảm số lượng ${line.sku_name}`}
          className="w-7 h-7 bg-stone-100 rounded-sm flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Minus className="w-3 h-3" aria-hidden="true" />
        </button>
        <span className="w-8 text-center text-sm font-bold" aria-live="polite">{line.qty}</span>
        <button
          type="button"
          onClick={() => onQtyChange(line.sku_id, 1)}
          aria-label={`Tăng số lượng ${line.sku_name}`}
          className="w-7 h-7 bg-stone-100 rounded-sm flex items-center justify-center"
        >
          <Plus className="w-3 h-3" aria-hidden="true" />
        </button>
      </div>
      <div className="text-right min-w-[80px]">
        <div className="font-mono text-sm font-bold">{fmtMoney(line.qty * line.unit_price)}</div>
      </div>
      <button
        type="button"
        onClick={() => onRemove(line.sku_id)}
        aria-label={`Xóa ${line.sku_name} khỏi giỏ`}
        className="text-stone-400 hover:text-red-600"
      >
        <X className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
